import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import {
  IndividualAccountKycData,
  IndividualAccountProfilesDatum,
} from '@core/models/admin/account';
import { AccountManagementService } from '@core/services/account-management.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { LoadingService } from '@core/services/loading.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { BehaviorSubject, Observable, Subscription, switchMap } from 'rxjs';
import { PermissionDirective } from '@shared/directives/permission.directive';

@Component({
  selector: 'app-profile-accounts',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    ButtonDirective,
    NgxSkeletonLoaderModule,
    PermissionDirective,
  ],
  templateUrl: './profile-accounts.component.html',
  styleUrl: './profile-accounts.component.scss',
})
export class ProfileAccountsComponent {
  @Input() type!: string;

  profileId!: string;
  accountId!: string;
  subs$: Subscription[] = [];
  accountsUrl!: string;
  userDetailsUrl!: string;
  isLoadingInfo$!: Observable<boolean>;
  isLoadingProfile$!: Observable<boolean>;
  userAccounts$!: Observable<IndividualAccountProfilesDatum[]>;
  private accountDataSubject =
    new BehaviorSubject<IndividualAccountKycData | null>(null);
  user$ = this.accountDataSubject.asObservable();
  private accountProfilesSubject = new BehaviorSubject<
    IndividualAccountProfilesDatum[] | null
  >(null);
  profiles$ = this.accountProfilesSubject.asObservable();

  private route = inject(ActivatedRoute);
  private accountService = inject(AccountManagementService);
  private router = inject(Router);
  private breadCrumbService = inject(BreadCrumbService);
  private loadingService = inject(LoadingService);

  constructor() {
    this.isLoadingInfo$ = this.loadingService.getLoadingObservable(
      'get-individual-account-kyc'
    );
    this.isLoadingProfile$ = this.loadingService.getLoadingObservable(
      'get-profile-accounts'
    );
  }

  ngOnInit(): void {
    this.breadCrumbService.setPageBreadCrumb(this.route);
    this.accountId = this.route.snapshot.params['accountId'];
    if (this.accountId) {
      this.getProfileInfo();
      this.accountsUrl = `/admin/accounts/${this.type}/accounts/${this.accountId}`;
      this.userDetailsUrl = `/admin/accounts/${this.type}/${this.accountId}/details`;
    }
  }

  getProfileInfo() {
    let accountInfo$;
    if (this.type === 'joint') {
      accountInfo$ = this.accountService.getJointAccountInfo(this.accountId);
    } else {
      accountInfo$ = this.accountService.getIndividualAccountInfo(
        this.accountId
      );
    }

    const sub$ = accountInfo$
      .pipe(
        switchMap((accountInfo) => {
          this.accountDataSubject.next(accountInfo.data);
          return this.accountService.getAccountProfiles(
            accountInfo.data.user.id
          );
        })
      )
      .subscribe({
        next: (acctProfileResponse) => {
          this.accountProfilesSubject.next(acctProfileResponse.data);
        },
      });

    this.subs$.push(sub$);
  }

  editAccount() {
    this.router.navigateByUrl(
      `/admin/accounts/${this.type}/edit/${this.accountId}`
    );
  }

  createMinorAccount() {
    this.router.navigateByUrl(
      `/admin/accounts/individual/accounts/${this.accountId}/create-minor-account`
    );
  }

  hasIndividualAccount(
    profiles: IndividualAccountProfilesDatum[] | null
  ): boolean {
    if (!profiles) return false;
    return profiles.some((p) => p.account_type === 'individual');
  }

  getProfileAccountName(profile: IndividualAccountProfilesDatum): string {
    if (profile.account_type === 'corporate') {
      return profile.corporate_kyc?.name || 'Corporate Account';
    }

    if (profile.account_type === 'joint') {
      const jointMembers = profile.joint_kyc ?? [];
      const names = jointMembers
        .map((member) =>
          [
            member?.personal_details?.first_name,
            member?.personal_details?.middle_name,
            member?.personal_details?.last_name,
          ]
            .filter(Boolean)
            .join(' ')
        )
        .filter(Boolean);

      return names.length ? names.join(' & ') : 'Joint Account';
    }

    const fullName = [
      profile.user_profile?.first_name,
      profile.user_profile?.middle_name,
      profile.user_profile?.last_name,
    ]
      .filter(Boolean)
      .join(' ');

    return fullName || 'Individual Account';
  }

  getProfileAccountTypeLabel(profile: IndividualAccountProfilesDatum): string {
    switch (profile.account_type) {
      case 'corporate':
        return 'Company Account';
      case 'joint':
        return 'Joint Account';
      default:
        return 'Individual Account';
    }
  }

  getProfileAccountRoute(profile: IndividualAccountProfilesDatum): any[] {
    return ['/admin/accounts', profile.account_type || 'individual', profile.id];
  }

  goBack() {
    history.back();
  }

  ngOnDestroy(): void {
    this.subs$.forEach((sub) => sub.unsubscribe());
  }
}
